/** R6 — le nombre de séances d'une ligne « à la séance » (kiné). */
export const SEANCES_MIN = 1;
export const SEANCES_MAX = 20;
/** R12 — un motif d'annulation se lit : cinq caractères au moins. */
export const MOTIF_ANNULATION_MIN = 5;
/**
 * R3, décisions 8 et 69 — la fiche attend la chargée de clientèle : la sienne
 * n'est pas confirmée, ou c'est un ayant droit dont le titulaire attend.
 */
export function attendLaChargee(fiche) {
    return (fiche.etatConfirmation === 'A_CONFIRMER' ||
        fiche.eligibilite?.motif === 'EN_ATTENTE_DE_CONFIRMATION');
}
export function situationDeCaisse(fiche) {
    if (attendLaChargee(fiche))
        return 'A_CONFIRMER';
    return fiche.parcours === 'PRIS_EN_CHARGE' ? 'COUVERT' : 'A_REGLER';
}
/**
 * R6, R9 — montant = prix × séances, monnaie = reçu − montant. Le montant ne
 * se saisit jamais : il vient de la grille. Une somme reçue plus petite que
 * le montant est refusée.
 */
export function calculerEncaissement(entree) {
    let seances = 1;
    if (entree.unite === 'SEANCE') {
        const s = entree.seances;
        if (s === undefined || !Number.isInteger(s) || s < SEANCES_MIN || s > SEANCES_MAX) {
            return {
                ok: false,
                motif: `Le nombre de séances est un entier de ${String(SEANCES_MIN)} à ${String(SEANCES_MAX)}.`,
            };
        }
        seances = s;
    }
    const montant = entree.prix * seances;
    if (!Number.isSafeInteger(montant) || !Number.isSafeInteger(entree.recu)) {
        return { ok: false, motif: 'Montant hors limites.' };
    }
    if (entree.recu < montant)
        return { ok: false, motif: 'Somme insuffisante.' };
    return { ok: true, seances, montant, rendu: entree.recu - montant };
}
/**
 * Décision 338 — le prix d'une ligne de la grille dans un centre : celui que
 * la Direction a posé pour ce centre, sinon le prix global. Sans centre (un
 * compte de la Direction), le prix global.
 */
export function prixAuCentre(ligne, siteId) {
    if (siteId === null)
        return ligne.prix;
    return ligne.prixParCentre.find((p) => p.siteId === siteId)?.prix ?? ligne.prix;
}
/** R11 — `<CODE CENTRE>-<AAAA>-<NNNNNN>` : six chiffres, sans trou. */
export function numeroDeRecu(codeCentre, annee, rang) {
    return `${codeCentre}-${String(annee)}-${String(rang).padStart(6, '0')}`;
}
/**
 * L'étape lue sur le statut de la visite liée (`undefined` : aucune visite).
 * Une seule lecture pour l'annulation (R12) et pour l'écran de la Direction,
 * qui ne propose « Annuler » que là où le serveur l'accepte : un statut de
 * visite ajouté plus tard compte comme « commencée », jamais comme annulable.
 */
export function etapeConsultation(statut) {
    if (statut === undefined || statut === 'ANNULEE')
        return 'AUCUNE';
    if (statut === 'ABSENT')
        return 'ABSENT';
    return statut === 'EN_ATTENTE' ? 'EN_ATTENTE' : 'COMMENCEE';
}
export function etatDuRecu(r) {
    if (!r.annule)
        return 'VALIDE';
    if (!r.annuleApresCloture)
        return 'ANNULE';
    return r.rendu ? 'RENDU' : 'A_RENDRE';
}
/**
 * R12 — qui annule quoi. Rend le motif du refus, ou `null` :
 *
 * - jamais un reçu déjà annulé, ni un reçu dont la consultation a commencé ;
 * - jamais le reçu d'un patient absent à l'appel (décision 60) : il reste
 *   réutilisable à tout moment, il n'est pas remboursable ;
 * - une caissière du centre : tout reçu du centre saisi aujourd'hui, le sien
 *   comme celui d'une collègue, caisse du centre encore ouverte (décisions
 *   61, 64 : le journal garde qui a encaissé et qui a annulé) ;
 * - la Direction (`caisse:annuler_cloture`) : après la clôture seulement —
 *   avant, c'est la caissière qui annule.
 */
export function refusAnnulation(c) {
    if (c.dejaAnnule)
        return { raison: 'DEJA_ANNULE', motif: 'Ce reçu est déjà annulé.' };
    if (c.consultation === 'ABSENT') {
        return {
            raison: 'PATIENT_ABSENT',
            motif: 'Le patient était absent à l’appel : son reçu reste valable pour revenir, à tout moment, et ne se rembourse pas.',
        };
    }
    if (c.consultation === 'COMMENCEE') {
        return {
            raison: 'CONSULTATION_COMMENCEE',
            motif: 'La consultation a commencé : ce reçu ne s’annule plus.',
        };
    }
    if (c.caisseCloturee) {
        return c.parLaDirection
            ? null
            : {
                raison: 'CAISSE_CLOTUREE',
                motif: 'La caisse est clôturée : seule la Direction annule ce reçu.',
            };
    }
    if (c.parLaDirection) {
        return {
            raison: 'AVANT_CLOTURE',
            motif: 'Avant la clôture, c’est une caissière du centre qui annule ce reçu.',
        };
    }
    if (!c.saisiAujourdhui) {
        return {
            raison: 'PAS_DU_JOUR',
            motif: 'Un reçu s’annule le jour même : une fois la caisse de ce jour clôturée, la Direction annulera.',
        };
    }
    return null;
}
/**
 * R13 — la clôture : écart = compté − attendu. Un écart, ou une caisse
 * oubliée (clôturée un jour suivant), exige un commentaire.
 */
export function calculerCloture(c) {
    const ecart = c.compte - c.attendu;
    const commente = (c.commentaire ?? '').trim() !== '';
    if (ecart !== 0 && !commente) {
        return { ok: false, motif: 'Un écart se commente : dites ce qui l’explique.' };
    }
    if (c.enRetard && !commente) {
        return { ok: false, motif: 'Une caisse d’un jour passé se clôture avec un commentaire.' };
    }
    return { ok: true, ecart };
}
//# sourceMappingURL=caisse.js.map